import { capturePosterCanvas, downloadBlob } from './exportBoardResultsPosters';

export const SLIDE_SECONDS = 1.5;
export const FADE_SECONDS = 0.45;
export const VIDEO_WIDTH = 1080;
export const VIDEO_HEIGHT = 1920;

function pickRecorderMime(hasAudio) {
  const types = hasAudio
    ? [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9',
        'video/webm',
        'video/mp4'
      ]
    : [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4'
      ];
  return types.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) || '';
}

export function videoFilename({ level, part, year, groupLabel } = {}) {
  const bits = [groupLabel, level, part, year]
    .map((v) => String(v || '').trim().replace(/[^\w-]+/g, '_'))
    .filter(Boolean);
  return `posters_video${bits.length ? `_${bits.join('_')}` : ''}.webm`;
}

export async function capturePosterFrames(elements, onProgress) {
  const frames = [];
  for (let i = 0; i < elements.length; i += 1) {
    onProgress?.({ phase: 'capture', current: i + 1, total: elements.length });
    const canvas = await capturePosterCanvas(elements[i]);
    frames.push(canvas);
  }
  return frames;
}

function waitFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function easeInOut(t) {
  return t * t * (3 - 2 * t);
}

function drawCover(ctx, img, zoom) {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const dw = w * zoom;
  const dh = h * zoom;
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/**
 * Record a 9:16 slideshow with a short slow crossfade between posters.
 */
export async function recordPosterSlideshow({
  frames,
  audioFile,
  secondsPerSlide = SLIDE_SECONDS,
  fadeSeconds = FADE_SECONDS,
  onProgress,
  isCancelled
}) {
  if (!frames?.length) throw new Error('No posters to record');
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('This browser cannot record video');
  }

  const mimeType = pickRecorderMime(Boolean(audioFile));
  const out = document.createElement('canvas');
  out.width = VIDEO_WIDTH;
  out.height = VIDEO_HEIGHT;
  Object.assign(out.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '2px',
    height: '2px',
    opacity: '0.01',
    pointerEvents: 'none',
    zIndex: '1'
  });
  document.body.appendChild(out);
  const ctx = out.getContext('2d', { alpha: false });
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);

  const videoStream = out.captureStream(30);
  const audioEl = document.createElement('audio');
  audioEl.preload = 'auto';
  audioEl.loop = true;
  audioEl.crossOrigin = 'anonymous';
  let audioUrl = null;
  let audioCtx = null;

  const tracks = [...videoStream.getVideoTracks()];

  if (audioFile) {
    audioUrl = URL.createObjectURL(audioFile);
    audioEl.src = audioUrl;
    audioEl.muted = false;
    audioEl.volume = 1;
    try {
      await audioEl.play();
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const src = audioCtx.createMediaElementSource(audioEl);
      const dest = audioCtx.createMediaStreamDestination();
      src.connect(dest);
      src.connect(audioCtx.destination);
      dest.stream.getAudioTracks().forEach((t) => tracks.push(t));
    } catch (err) {
      console.warn('[PosterVideo] audio mix failed:', err.message);
    }
  }

  const mixed = new MediaStream(tracks);
  const recorder = new MediaRecorder(mixed, mimeType ? { mimeType, videoBitsPerSecond: 6_000_000 } : { videoBitsPerSecond: 6_000_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size) chunks.push(e.data);
  };

  const stopped = new Promise((resolve, reject) => {
    recorder.onstop = () => resolve();
    recorder.onerror = (e) => reject(e.error || new Error('Recording failed'));
  });

  const fadeMs = Math.min(fadeSeconds, secondsPerSlide * 0.45) * 1000;
  const holdMs = Math.max(250, secondsPerSlide * 1000 - fadeMs);

  const paintHold = async (img, ms) => {
    const until = performance.now() + ms;
    while (performance.now() < until) {
      if (isCancelled?.()) throw new Error('cancelled');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(img, 0, 0, out.width, out.height);
      await waitFrame();
    }
  };

  const paintFade = async (from, to, ms) => {
    const t0 = performance.now();
    while (true) {
      if (isCancelled?.()) throw new Error('cancelled');
      const t = Math.min(1, (performance.now() - t0) / ms);
      const e = easeInOut(t);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.save();
      ctx.globalAlpha = 1 - e;
      drawCover(ctx, from, 1 + 0.035 * e);
      ctx.restore();
      ctx.save();
      ctx.globalAlpha = e;
      drawCover(ctx, to, 1.035 - 0.035 * e);
      ctx.restore();
      await waitFrame();
      if (t >= 1) break;
    }
  };

  recorder.start(250);

  try {
    for (let i = 0; i < frames.length; i += 1) {
      if (isCancelled?.()) throw new Error('cancelled');
      onProgress?.({ phase: 'record', current: i + 1, total: frames.length });
      const isLast = i === frames.length - 1;
      await paintHold(frames[i], isLast ? secondsPerSlide * 1000 : holdMs);
      if (!isLast) await paintFade(frames[i], frames[i + 1], fadeMs);
    }
  } finally {
    if (recorder.state !== 'inactive') recorder.stop();
    audioEl.pause();
    audioEl.removeAttribute('src');
    audioEl.load();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    if (audioCtx) {
      try { await audioCtx.close(); } catch { /* ignore */ }
    }
    videoStream.getTracks().forEach((t) => t.stop());
    mixed.getTracks().forEach((t) => t.stop());
    out.remove();
  }

  await stopped;
  const type = recorder.mimeType || mimeType || 'video/webm';
  if (!chunks.length) throw new Error('Recording produced an empty file');
  return new Blob(chunks, { type });
}

export { downloadBlob };
